import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, FormControlLabel, Switch, TextField, Typography } from '@mui/material';
import TurnstileWidget from '../components/TurnstileWidget';
import { signIn } from '../utils/api';

const SASSY_ERROR_MESSAGES = [
  "Nope, that's not right.",
  'Sorry, please try again.',
  'You really thought that was right?',
];

const SignIn = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [turnstileToken, setTurnstileToken] = useState('');

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!turnstileToken) {
      setErrorMessage('Please wait for the security check to finish.');
      return;
    }

    setIsSubmitting(true);
    try {
      await signIn(username, password, rememberMe, turnstileToken);
      setErrorMessage('');
      setFailedAttempts(0);
      const nextPath = searchParams.get('next');
      navigate(nextPath?.startsWith('/') && !nextPath.startsWith('//') ? nextPath : '/user-home');
    } catch (error) {
      if (error.status === 401) {
        setErrorMessage(SASSY_ERROR_MESSAGES[failedAttempts % SASSY_ERROR_MESSAGES.length]);
        setFailedAttempts((previousAttempts) => previousAttempts + 1);
      } else {
        setErrorMessage(error.data?.message || 'Sign in could not be completed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
      setTurnstileToken('');
      setTurnstileResetKey((currentKey) => currentKey + 1);
    }
  };

  return (
    <Box
      id="sign-in-page"
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        background:
          'radial-gradient(circle at top, rgba(129,236,255,0.18) 0%, rgba(11,14,20,1) 45%), linear-gradient(135deg, rgba(255,105,166,0.14) 0%, rgba(11,14,20,1) 50%)',
      }}
    >
      <Box
        id="sign-in-card"
        sx={{
          width: '100%',
          maxWidth: 500,
          borderRadius: 4,
          p: { xs: 3, sm: 4 },
          background:
            'linear-gradient(135deg, rgba(129,236,255,0.2) 0%, rgba(11,14,20,0.92) 42%, rgba(11,14,20,0.96) 100%)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 0 40px rgba(129,236,255,0.2), 0 0 56px rgba(255,105,166,0.15)',
        }}
      >
        <Typography
          variant="h3"
          sx={{
            mb: 1,
            textAlign: 'left',
            color: 'primary.main',
            textShadow: '0 0 12px rgba(129,236,255,0.4)',
          }}
        >
          Sign In
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', textAlign: 'left' }}>
          Welcome back. Let&apos;s get those tasks handled.
        </Typography>

        <Box id="sign-in-form" component="form" onSubmit={handleSignIn} sx={{ display: 'grid', gap: 2.5 }}>
          <TextField
            type="text"
            id="sign-in-username"
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            fullWidth
            variant="outlined"
          />


          <TextField
            type="password"
            id="sign-in-password"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
            variant="outlined"
          />
          <FormControlLabel
            control={(
              <Switch
                id="sign-in-remember-me"
                checked={rememberMe}
                onChange={(event) => setRememberMe(event.target.checked)}
              />
            )}
            label="Remember me"
          />

          <TurnstileWidget
            action="login"
            resetKey={turnstileResetKey}
            onVerify={setTurnstileToken}
            onExpire={() => setTurnstileToken('')}
            onError={() => setTurnstileToken('')}
          />

          {errorMessage && (
            <Alert
              severity="error"
              sx={{
                backgroundColor: 'rgba(255, 105, 166, 0.18)',
                color: 'text.primary',
                '& .MuiAlert-icon': { color: 'secondary.main' },
              }}
            >
              {errorMessage}
            </Alert>
          )}

          <Button
            id="sign-in-submit"
            type="submit"
            variant="contained"
            disabled={isSubmitting || !turnstileToken}
            sx={{
              mt: 1,
              py: 1.4,
              background: 'linear-gradient(135deg, #81ecff 0%, #ff69a6 100%)',
              color: '#0b0e14',
              boxShadow: '0 6px 0 rgba(48, 118, 140, 0.95), 0 0 26px rgba(129,236,255,0.35)',
              '&:hover': {
                background: 'linear-gradient(135deg, #91f4ff 0%, #ff84b8 100%)',
              },
              '&:active': {
                transform: 'translateY(3px)',
                boxShadow: '0 2px 0 rgba(48, 118, 140, 0.95), 0 0 20px rgba(129,236,255,0.25)',
              },
            }}
          >
            {isSubmitting ? 'Signing In…' : 'Sign In'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default SignIn;
